import {
  useEffect,
  useState,
} from "react";

import {
  loadHistoricoAluno,
} from "../turmas.service";

import type {
  HistoricoAlunoResumo,
} from "../turmas.service";

import type {
  Aluno,
} from "../types";

import "./HistoricoAluno.css";


type HistoricoAlunoProps = {
  aluno: Aluno;

  turmaId: string;

  refreshKey?: number;

  onAbrirChamada: (
    data: string,
  ) => void;
};


function formatDateBR(
  value: string,
) {
  const [
    year,
    month,
    day,
  ] =
    value.split("-");

  return `${day}/${month}/${year}`;
}


function formatHour(
  value: number,
) {
  const hours =
    Math.floor(
      value,
    );

  const minutes =
    Math.round(
      (
        value -
        hours
      ) *
        60,
    );

  return `${String(
    hours,
  ).padStart(
    2,
    "0",
  )}:${String(
    minutes,
  ).padStart(
    2,
    "0",
  )}`;
}


function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(value);
}


export default function HistoricoAluno({
  aluno,
  turmaId,
  refreshKey = 0,
  onAbrirChamada,
}: HistoricoAlunoProps) {
  const [
    historico,
    setHistorico,
  ] =
    useState<
      HistoricoAlunoResumo | null
    >(null);


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    error,
    setError,
  ] =
    useState("");


  useEffect(() => {
    let active =
      true;


    async function load() {
      try {
        setLoading(
          true,
        );

        setError("");


        const result =
          await loadHistoricoAluno(
            aluno.id,
            turmaId,
          );


        if (!active) {
          return;
        }


        setHistorico(
          result,
        );
      } catch (err) {
        console.error(
          err,
        );


        if (active) {
          setError(
            "Não foi possível carregar o histórico do aluno.",
          );
        }
      } finally {
        if (active) {
          setLoading(
            false,
          );
        }
      }
    }


    load();


    return () => {
      active =
        false;
    };
  }, [
  aluno.id,
  turmaId,
  refreshKey,
]);


  if (loading) {
    return (
      <div className="historico-aluno-loading">
        Carregando histórico...
      </div>
    );
  }


  if (error) {
    return (
      <div className="turmas-form-error">
        {error}
      </div>
    );
  }


  if (!historico) {
    return null;
  }


  return (
    <section className="historico-aluno">
      <div className="historico-aluno-header">
        <div>
          <span className="turmas-eyebrow">
            HISTÓRICO DO ALUNO
          </span>

          <h4>
            {aluno.nome}
          </h4>
        </div>
      </div>


      <div className="historico-aluno-resumo">
        <div>
          <span>
            Aulas
          </span>

          <strong>
            {
              historico.totalAulas
            }
          </strong>
        </div>

        <div>
          <span>
            Presenças
          </span>

          <strong>
            {
              historico.presencas
            }
          </strong>
        </div>

        <div>
          <span>
            Faltas
          </span>

          <strong>
            {
              historico.faltas
            }
          </strong>
        </div>

        <div>
          <span>
            Frequência
          </span>

          <strong>
            {historico.frequencia.toFixed(
              1,
            )}
            %
          </strong>
        </div>
      </div>


      <div className="historico-aluno-financeiro">
        <div>
          <span>
            Aulas de pacote
          </span>

          <strong>
            {
              historico.aulasPacote
            }
          </strong>
        </div>

        <div>
          <span>
            Aulas avulsas
          </span>

          <strong>
            {
              historico.aulasAvulsas
            }
          </strong>
        </div>

        <div>
          <span>
            Valor avulsas
          </span>

          <strong>
            {formatBRL(
              historico.valorAvulsas,
            )}
          </strong>
        </div>
      </div>


      {historico.aulas.length ===
      0 ? (
        <div className="turmas-empty">
          Nenhuma aula registrada
          para este aluno.
        </div>
      ) : (
        <div className="historico-aluno-list">
          {historico.aulas.map(
            (aula) => (
              <div
                key={
                  aula.aulaId
                }
                className="historico-aluno-aula"
              >
                <div className="historico-aluno-aula-main">
                  <strong>
                    {formatDateBR(
                      aula.data,
                    )}
                  </strong>

                  <span>
                    {formatHour(
                      aula.hora,
                    )}
                  </span>
                </div>


                <div className="historico-aluno-aula-status">
                  <span
                    className={
                      aula.presente
                        ? "historico-status-presente"
                        : "historico-status-falta"
                    }
                  >
                    {aula.presente
                      ? "Presente"
                      : "Faltou"}
                  </span>

                  <span>
                    {aula.tipoPagamento ===
                    "pacote"
                      ? "Pacote"
                      : "Avulsa"}
                  </span>

                  {aula.tipoPagamento ===
                    "avulsa" && (
                    <span>
                      {formatBRL(
                        aula.valorAvulso,
                      )}
                    </span>
                  )}

                  {aula.tipoPagamento ===
                    "avulsa" && (
                    <span>
                      {aula.pago
                        ? `Pago${
                            aula.formaPagamento
                              ? ` • ${aula.formaPagamento}`
                              : ""
                          }`
                        : "Pendente"}
                    </span>
                  )}
                </div>


                <button
                  type="button"
                  className="historico-aluno-open"
                  onClick={() =>
                    onAbrirChamada(
                      aula.data,
                    )
                  }
                >
                  Ver chamada
                </button>
              </div>
            ),
          )}
        </div>
      )}
    </section>
  );
}